// Meta Event Handlers Service - Main Tracking Functions
// Extracted from metaTracking.js for better separation of concerns

import { debugLog, debugError } from '../debugUtils';
import {
  getFacebookTrackingParams,
  clearFacebookTrackingSession,
} from './fbpTracking';
import {
  validateConfiguration,
  hashEmail,
  cleanPayload,
  fetchWithRetry,
  generateEventId,
  shouldSkipTracking,
  getMetaPixelId,
  getMetaPixel,
  storeUserDataForTracking,
} from './analyticsUtils';

/**
 * Send Lead event to both Meta Pixel and Conversions API with same event ID
 */
export const trackLead = async data => {
  debugLog(`🎯 trackLead called with data:`, {
    email: data.email ? `${data.email.substring(0, 3)}***` : 'no email',
    source: data.source,
    hasEventId: !!data.event_id,
  });

  // Early return for preview/internal domains
  const skipCheck = shouldSkipTracking();
  if (skipCheck.skip) {
    return {
      success: false,
      error: skipCheck.reason,
    };
  }

  // Validate configuration before proceeding
  const isValid = validateConfiguration();
  if (!isValid && process.env.NODE_ENV === 'production') {
    return { success: false, error: 'Configuration issues detected' };
  }

  try {
    const { fbc, fbp } = await getFacebookTrackingParams();

    // Generate event ID
    let eventId;
    try {
      eventId = data.event_id || generateEventId('Lead', data.email);
    } catch (eventIdError) {
      debugError(`Cannot track Lead event: ${eventIdError.message}`);
      return {
        success: false,
        error: `Lead tracking failed: ${eventIdError.message}`,
        reason: 'missing_identifier',
      };
    }

    // Hash email for Meta Pixel
    const hashedEmail = await hashEmail(data.email);

    // Pixel event data (standardized with email forms)
    const pixelEventData = {
      content_category: data.content_category || 'email_opt_in',
      content_name: data.content_name || 'Email Signup',
      content_type: data.content_type || 'lead_form',
      source: data.source || 'website',
      currency: data.currency || 'USD', // Add currency parameter for better campaign optimization
    };
    if (hashedEmail) {
      pixelEventData.em = hashedEmail;
    }

    // API payload - Include ALL available user data for maximum attribution
    const payload = {
      event_type: 'Lead',
      pixel_id: getMetaPixelId(),
      email: data.email,
      currency: data.currency || 'USD', // Add currency parameter for better campaign optimization
      event_source_url:
        typeof window !== 'undefined' ? window.location.href : data.source_url,
      event_id: eventId,
      fbc: fbc,
      fbp: fbp,
      // Include content metadata for better tracking
      content_category: data.content_category || 'email_opt_in',
      content_name: data.content_name || 'Email Signup',
      content_type: data.content_type || 'lead_form',
      ...data,
    };

    if (
      process.env.NODE_ENV === 'development' &&
      process.env.GATSBY_META_TEST_EVENT_CODE
    ) {
      payload.test_event_code = process.env.GATSBY_META_TEST_EVENT_CODE;
    }

    // Store user data
    await storeUserDataForTracking(data);

    debugLog(`🔄 Sending Lead event with ID ${eventId}...`);

    // Execute both calls
    const results = await Promise.allSettled([
      // Meta Pixel
      new Promise((resolve, reject) => {
        const fbq = getMetaPixel();
        if (fbq) {
          try {
            if (hashedEmail) {
              pixelEventData.em = hashedEmail;
            }

            // CRITICAL: Explicitly pass FBC and FBP to prevent cookie modification issues
            if (fbc) {
              pixelEventData.fbc = fbc;
            }
            if (fbp) {
              pixelEventData.fbp = fbp;
            }

            fbq('track', 'Lead', pixelEventData, { eventID: eventId });
            debugLog(`✅ Meta Pixel Lead event sent`);
            resolve('pixel_success');
          } catch (error) {
            debugError(`❌ Meta Pixel Lead failed:`, error);
            reject(error);
          }
        } else {
          resolve('pixel_skipped');
        }
      }),

      // Conversions API
      (async () => {
        try {
          const response = await fetchWithRetry(
            '/.netlify/functions/meta-conversion-tracking',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(cleanPayload(payload)),
            }
          );

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
          }

          await response.json();
          debugLog(`✅ Meta Conversions API Lead event sent`);
          return 'api_success';
        } catch (error) {
          debugError(`❌ Meta Conversions API Lead failed:`, error);
          throw error;
        }
      })(),
    ]);

    debugLog(`📊 Lead tracking completed:`, { eventId, results });

    return { success: true, event_id: eventId };
  } catch (error) {
    debugError(`Error tracking Meta Lead event:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Send Purchase event to both Meta Pixel and Conversions API with same event ID
 */
export const trackPurchase = async data => {
  debugLog(`🛒 trackPurchase called with data:`, {
    email: data.email ? `${data.email.substring(0, 3)}***` : 'no email',
    value: data.value,
    currency: data.currency,
    quantity: data.quantity,
    hasEventId: !!data.event_id,
  });

  const skipCheck = shouldSkipTracking();
  if (skipCheck.skip) {
    return { success: false, error: skipCheck.reason };
  }

  const isValid = validateConfiguration();
  if (!isValid && process.env.NODE_ENV === 'production') {
    return {
      success: false,
      error: 'Configuration issues detected',
    };
  }

  try {
    // Enhanced Facebook tracking parameter collection with retry logic
    debugLog(`🔍 Collecting Facebook tracking parameters for Purchase...`);
    let fbc = null,
      fbp = null,
      fbpSource = 'unavailable';

    try {
      const trackingParams = await getFacebookTrackingParams({
        eventType: 'Purchase',
        value: data.value,
        currency: data.currency,
      });
      fbc = trackingParams.fbc;
      fbp = trackingParams.fbp;
      fbpSource = trackingParams.fbpSource;

      debugLog(`📊 Facebook tracking params collected:`, {
        hasFbc: !!fbc,
        hasFbp: !!fbp,
        fbpSource: fbpSource,
        fbcLength: fbc ? fbc.length : 0,
        fbpLength: fbp ? fbp.length : 0,
      });
    } catch (fbParamsError) {
      debugError(`⚠️ Failed to get Facebook tracking params:`, fbParamsError);
      // Continue without Facebook params - email and other data still valuable
    }

    // Get email from data OR fallback to stored user data
    let email = data.email;
    let emailSource = 'provided';

    if (!email || email.trim() === '') {
      try {
        const { getUserData } = await import('../userDataPersistence');
        const storedUserData = getUserData();
        if (storedUserData && storedUserData.email) {
          email = storedUserData.email;
          emailSource = 'stored_fallback';
          debugLog(`🔄 Purchase event using stored email as fallback`);
        }
      } catch (error) {
        debugLog(
          `⚠️ Failed to load stored email for Purchase fallback:`,
          error
        );
      }
    }

    let eventId;
    try {
      eventId = data.event_id || generateEventId('Purchase', email);
      debugLog(`🔑 Generated event ID: ${eventId}`);
    } catch (eventIdError) {
      debugError(`❌ Purchase tracking failed: ${eventIdError.message}`);
      return {
        success: false,
        error: `Purchase tracking failed: ${eventIdError.message}`,
        reason: 'missing_identifier',
      };
    }

    const hashedEmail = await hashEmail(email);

    // Enhanced payload with flexible content metadata - support OTO flows
    const payload = {
      event_type: 'Purchase',
      pixel_id: getMetaPixelId(),
      email: email,
      value: data.value,
      currency: data.currency || 'USD',
      // Use passed content metadata OR defaults for backward compatibility
      content_type: data.content_type || 'product',
      content_ids: data.content_ids || ['karaoke-ticket'],
      content_name: data.content_name || 'Karaoke Event Ticket',
      content_category: data.content_category || 'event_tickets',
      // Include quantity as both quantity and num_items for maximum compatibility
      quantity: data.quantity || data.num_items || 1,
      num_items: data.num_items || data.quantity || 1,
      event_source_url:
        typeof window !== 'undefined' ? window.location.href : data.source_url,
      event_id: eventId,
      fbc: fbc,
      fbp: fbp,
      email_source: emailSource,
      ...data,
    };

    if (
      process.env.NODE_ENV === 'development' &&
      process.env.GATSBY_META_TEST_EVENT_CODE
    ) {
      payload.test_event_code = process.env.GATSBY_META_TEST_EVENT_CODE;
    }

    debugLog(`🚀 Sending Purchase event to Meta with payload summary:`, {
      eventId,
      email: email ? `${email.substring(0, 3)}***` : 'no email',
      value: data.value,
      currency: data.currency || 'USD',
      hasFbc: !!fbc,
      hasFbp: !!fbp,
      fbpSource: fbpSource,
    });

    const results = await Promise.allSettled([
      // Meta Pixel
      new Promise((resolve, reject) => {
        const fbq = getMetaPixel();
        if (fbq) {
          try {
            const pixelEventData = {
              value: data.value,
              currency: data.currency || 'USD',
              content_type: 'product',
              content_ids: ['karaoke-ticket'],
              content_name: 'Karaoke Event Ticket',
              content_category: 'event_tickets',
              num_items: data.num_items || data.quantity || 1,
            };

            if (hashedEmail) {
              pixelEventData.em = hashedEmail;
            }

            // CRITICAL: Explicitly pass FBC and FBP to prevent cookie modification issues
            if (fbc) {
              pixelEventData.fbc = fbc;
            }
            if (fbp) {
              pixelEventData.fbp = fbp;
            }

            fbq('track', 'Purchase', pixelEventData, { eventID: eventId });
            debugLog(`✅ Meta Pixel Purchase event sent`);
            resolve('pixel_success');
          } catch (error) {
            debugError(`❌ Meta Pixel Purchase failed:`, error);
            reject(error);
          }
        } else {
          debugLog(`⚠️ Meta Pixel not available, skipping pixel tracking`);
          resolve('pixel_skipped');
        }
      }),

      // Conversions API
      (async () => {
        try {
          const response = await fetchWithRetry(
            '/.netlify/functions/meta-conversion-tracking',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(cleanPayload(payload)),
            }
          );

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
          }

          await response.json();
          debugLog(`✅ Meta Conversions API Purchase event sent`);
          return 'api_success';
        } catch (error) {
          debugError(`❌ Meta Conversions API Purchase failed:`, error);
          throw error;
        }
      })(),
    ]);

    debugLog(`📊 Purchase tracking completed:`, { eventId, results });

    return { success: true, event_id: eventId };
  } catch (error) {
    debugError(`❌ Error tracking Meta Purchase event:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Send PageView event to both Meta Pixel and Conversions API with same event ID
 */
export const trackPageView = async (data = {}) => {
  const skipCheck = shouldSkipTracking();
  if (skipCheck.skip) {
    return { success: false, error: skipCheck.reason };
  }

  // More lenient validation for PageViews
  const _isValid = validateConfiguration();

  try {
    const { fbc, fbp, fbpSource } = await getFacebookTrackingParams({
      eventType: 'PageView',
      url: data.url,
    });

    const currentUrl =
      data.url || (typeof window !== 'undefined' ? window.location.href : null);
    let eventId;
    try {
      const identifier = currentUrl || `fallback_${Date.now()}`;
      eventId = data.event_id || generateEventId('PageView', identifier);
    } catch (eventIdError) {
      eventId = `pageview_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
    }

    // Get stored user data for PageView matching
    let storedUserData = null;
    try {
      const { getUserData } = await import('../userDataPersistence');
      storedUserData = getUserData();
    } catch (error) {
      debugLog(`⚠️ Failed to load user data for PageView matching:`, error);
    }

    const pixelEventData = {
      content_name:
        data.title || (typeof document !== 'undefined' ? document.title : ''),
      source: 'website',
    };

    // Enhanced payload with all available user data for maximum attribution
    const payload = {
      event_type: 'PageView',
      pixel_id: getMetaPixelId(),
      event_source_url: currentUrl,
      event_id: eventId,
      fbc: fbc,
      fbp: fbp,
      // Include stored user data (email, etc.) if available
      ...(storedUserData?.email && { email: storedUserData.email }),
      // Include page metadata for better tracking
      content_name:
        data.title || (typeof document !== 'undefined' ? document.title : ''),
      content_type: 'page',
      // Merge any additional data
      ...data,
    };

    if (
      process.env.NODE_ENV === 'development' &&
      process.env.GATSBY_META_TEST_EVENT_CODE
    ) {
      payload.test_event_code = process.env.GATSBY_META_TEST_EVENT_CODE;
    }

    // CRITICAL: Set up navigation interruption protection with sendBeacon
    let navigationProtectionCleanup = null;
    let trackingCompleted = false;
    let normalTrackingSucceeded = false; // Track if normal tracking succeeded

    if (typeof window !== 'undefined' && navigator.sendBeacon) {
      const sendBeaconFallback = () => {
        if (trackingCompleted) return; // Normal tracking already completed
        if (normalTrackingSucceeded) return; // Normal tracking succeeded, don't send backup

        debugLog(
          `🚨 Navigation interruption detected - collecting fresh FBC/FBP for beacon...`
        );

        // CRITICAL: Synchronous fresh FBP check at beacon time - pixel might be ready now!
        let beaconFbc = fbc; // Keep original FBC (more stable)
        let beaconFbp = fbp; // Start with original
        let beaconFbpSource = fbpSource;

        try {
          // Direct synchronous cookie check - much faster than full getFacebookTrackingParams
          const getCookie = name => {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop().split(';').shift();
            return null;
          };

          // Check for fresh FBP cookie
          const freshFbp = getCookie('_fbp');
          if (freshFbp && !fbp) {
            beaconFbp = freshFbp;
            beaconFbpSource = 'Meta native _fbp (beacon fresh)';
            debugLog(`✅ Beacon found fresh FBP cookie: ${beaconFbpSource}`);
          } else if (freshFbp && fbp) {
            debugLog(`📊 Beacon: both original and fresh FBP available`);
          } else if (!freshFbp && !fbp) {
            debugLog(`⚠️ Beacon: no FBP available (original or fresh)`);
          }

          // Check for fresh FBC cookie - always check since FBC is critical for attribution
          const freshFbc = getCookie('_fbc');
          const fbcQualityCheck = fbcValue => {
            if (!fbcValue) return { valid: false, score: 0 };
            return {
              valid: fbcValue.startsWith('fb.'),
              score: fbcValue.startsWith('fb.')
                ? fbcValue.includes('%') ||
                  fbcValue.includes(' ') ||
                  fbcValue.includes('+')
                  ? 1
                  : 2
                : 0,
              hasEncoding:
                fbcValue.includes('%') ||
                fbcValue.includes(' ') ||
                fbcValue.includes('+'),
            };
          };

          const originalFbcQuality = fbcQualityCheck(fbc);
          const freshFbcQuality = fbcQualityCheck(freshFbc);

          // Use fresh FBC if it's better than original, or if we had no original
          if (
            freshFbc &&
            (!fbc || freshFbcQuality.score > originalFbcQuality.score)
          ) {
            if (!fbc) {
              beaconFbc = freshFbc;
              debugLog(`✅ Beacon found fresh FBC cookie (was missing)`);
            } else if (freshFbcQuality.score > originalFbcQuality.score) {
              beaconFbc = freshFbc;
              debugLog(
                `✅ Beacon upgraded FBC quality: ${originalFbcQuality.score} → ${freshFbcQuality.score}`
              );
            }
          } else if (freshFbc && fbc) {
            debugLog(
              `📊 Beacon: both original and fresh FBC available (keeping original)`
            );
          } else if (!freshFbc && !fbc) {
            debugLog(`⚠️ Beacon: no FBC available (original or fresh)`);
          }
        } catch (error) {
          debugLog(
            `⚠️ Beacon fresh cookie check failed, using original:`,
            error
          );
        }

        // Prepare beacon payload with fresh attribution data
        const beaconPayload = {
          ...payload,
          fbc: beaconFbc, // Use fresh FBC if available
          fbp: beaconFbp, // Use fresh FBP if available
          beacon_fallback: true,
          navigation_interrupted: true,
          beacon_timestamp: Date.now(),
          beacon_fbp_source: beaconFbpSource,
          beacon_fbp_fresh: beaconFbp !== fbp, // Track if we got fresh FBP
          beacon_fbc_fresh: beaconFbc !== fbc, // Track if we got fresh FBC
          beacon_fbc_upgraded: beaconFbc && fbc && beaconFbc !== fbc, // Track FBC quality upgrade
          beacon_fbp_recovered: beaconFbp && !fbp, // Track FBP recovery
          beacon_fbc_recovered: beaconFbc && !fbc, // Track FBC recovery
        };

        debugLog(
          `📊 Beacon payload attribution: FBC=${!!beaconFbc} (${beaconFbc !== fbc ? 'FRESH' : 'original'}), FBP=${!!beaconFbp} (${beaconFbp !== fbp ? 'FRESH' : 'original'}), Email=${!!storedUserData?.email}`
        );

        try {
          // Send directly to meta-conversion-tracking with fresh data
          const success = navigator.sendBeacon(
            '/.netlify/functions/meta-conversion-tracking',
            JSON.stringify(cleanPayload(beaconPayload))
          );

          if (success) {
            debugLog(
              `✅ PageView beacon sent with ${beaconFbp !== fbp ? 'fresh' : 'original'} FBP data`
            );

            // Track beacon attribution recovery for analytics (async, won't block navigation)
            const fbpRecovered = beaconFbp && !fbp;
            const fbcRecovered = beaconFbc && !fbc;
            const fbcUpgraded = beaconFbc && fbc && beaconFbc !== fbc;

            if (fbpRecovered || fbcRecovered || fbcUpgraded) {
              // FBP/FBC recovery detected but meta_ event tracking removed
            }
          } else {
            debugLog(`❌ PageView beacon failed to send`);
          }
        } catch (error) {
          debugLog(`❌ PageView beacon error:`, error);
        }
      };

      // Add navigation listeners for interruption detection
      const cleanup = () => {
        window.removeEventListener('beforeunload', sendBeaconFallback);
        window.removeEventListener('pagehide', sendBeaconFallback);
        if (typeof document !== 'undefined') {
          document.removeEventListener(
            'visibilitychange',
            handleVisibilityChange
          );
        }
      };

      const handleVisibilityChange = () => {
        if (document.hidden && !trackingCompleted) {
          // Page became hidden - potential navigation
          setTimeout(sendBeaconFallback, 10); // Small delay to avoid false positives
        }
      };

      // Set up listeners
      window.addEventListener('beforeunload', sendBeaconFallback, {
        passive: true,
      });
      window.addEventListener('pagehide', sendBeaconFallback, {
        passive: true,
      });
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', handleVisibilityChange, {
          passive: true,
        });
      }

      navigationProtectionCleanup = cleanup;

      // Auto-cleanup after reasonable time (15 seconds) to prevent memory leaks
      setTimeout(() => {
        if (navigationProtectionCleanup) {
          navigationProtectionCleanup();
          navigationProtectionCleanup = null;
        }
      }, 15000);
    }

    const results = await Promise.allSettled([
      // Meta Pixel
      new Promise((resolve, reject) => {
        const fbq = getMetaPixel();
        if (fbq) {
          try {
            fbq('track', 'PageView', pixelEventData, { eventID: eventId });
            debugLog(`✅ Meta Pixel PageView event sent`);
            normalTrackingSucceeded = true;
            resolve('pixel_success');
          } catch (error) {
            debugError(`❌ Meta Pixel PageView failed:`, error);
            reject(error);
          }
        } else {
          resolve('pixel_skipped');
        }
      }),

      // Conversions API
      (async () => {
        try {
          const response = await fetchWithRetry(
            '/.netlify/functions/meta-conversion-tracking',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(cleanPayload(payload)),
            }
          );

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
          }

          await response.json();
          debugLog(`✅ Meta Conversions API PageView event sent`);
          normalTrackingSucceeded = true;
          return 'api_success';
        } catch (error) {
          debugError(`❌ Meta Conversions API PageView failed:`, error);
          throw error;
        }
      })(),
    ]);

    // Mark tracking as completed and cleanup navigation protection
    trackingCompleted = true;
    if (navigationProtectionCleanup) {
      navigationProtectionCleanup();
      navigationProtectionCleanup = null;
    }

    debugLog(`📊 PageView tracking completed:`, {
      eventId,
      results,
      normalTrackingSucceeded,
    });

    return { success: true, event_id: eventId };
  } catch (error) {
    debugError(`❌ Error tracking Meta PageView event:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Send InitiateCheckout event to both Meta Pixel and Conversions API with same event ID
 */
export const trackInitiateCheckout = async data => {
  const skipCheck = shouldSkipTracking();
  if (skipCheck.skip) {
    return { success: false, error: skipCheck.reason };
  }

  if (!validateConfiguration() && process.env.NODE_ENV === 'production') {
    return { success: false, error: 'Configuration issues detected' };
  }

  try {
    const { fbc, fbp } = await getFacebookTrackingParams();

    // Get email from data OR fallback to stored user data
    let email = data.email;
    let emailSource = 'provided';

    if (!email || email.trim() === '') {
      try {
        const { getUserData } = await import('../userDataPersistence');
        const storedUserData = getUserData();
        if (storedUserData && storedUserData.email) {
          email = storedUserData.email;
          emailSource = 'stored_fallback';
          debugLog(`🔄 InitiateCheckout event using stored email as fallback`);
        }
      } catch (error) {
        debugLog(
          `⚠️ Failed to load stored email for InitiateCheckout fallback:`,
          error
        );
      }
    }

    let eventId;
    try {
      eventId = data.event_id || generateEventId('InitiateCheckout', email);
    } catch (eventIdError) {
      debugError(
        `❌ Cannot track InitiateCheckout event: ${eventIdError.message}`
      );
      return {
        success: false,
        error: `InitiateCheckout tracking failed: ${eventIdError.message}`,
        reason: 'missing_identifier',
      };
    }

    const hashedEmail = await hashEmail(email);

    // Enhanced payload with complete content metadata
    const payload = {
      event_type: 'InitiateCheckout',
      pixel_id: getMetaPixelId(),
      email: email,
      value: data.value,
      currency: data.currency || 'USD',
      // Include content metadata for better tracking
      content_type: data.content_type || 'product',
      content_ids: data.content_ids || ['karaoke-ticket'],
      content_name: data.content_name || 'Karaoke Event Ticket',
      content_category: data.content_category || 'event_tickets',
      quantity: data.quantity || data.num_items || 1,
      num_items: data.num_items || data.quantity || 1,
      event_source_url:
        data.event_source_url ||
        (typeof window !== 'undefined' ? window.location.href : null),
      event_id: eventId,
      fbc: fbc,
      fbp: fbp,
      email_source: emailSource,
      ...data,
    };

    if (
      process.env.NODE_ENV === 'development' &&
      process.env.GATSBY_META_TEST_EVENT_CODE
    ) {
      payload.test_event_code = process.env.GATSBY_META_TEST_EVENT_CODE;
    }

    const results = await Promise.allSettled([
      // Meta Pixel
      new Promise((resolve, reject) => {
        const fbq = getMetaPixel();
        if (fbq) {
          try {
            const pixelEventData = {
              value: data.value,
              currency: data.currency || 'USD',
              content_type: data.content_type || 'product',
              content_ids: data.content_ids || ['karaoke-ticket'],
              content_name: data.content_name || 'Karaoke Event Ticket',
              content_category: data.content_category || 'event_tickets',
              num_items: data.num_items || data.quantity || 1,
            };

            if (hashedEmail) {
              pixelEventData.em = hashedEmail;
            }

            // CRITICAL: Explicitly pass FBC and FBP to prevent cookie modification issues
            if (fbc) {
              pixelEventData.fbc = fbc;
            }
            if (fbp) {
              pixelEventData.fbp = fbp;
            }

            fbq('track', 'InitiateCheckout', pixelEventData, {
              eventID: eventId,
            });
            debugLog(`✅ Meta Pixel InitiateCheckout event sent`);
            resolve('pixel_success');
          } catch (error) {
            debugError(`❌ Meta Pixel InitiateCheckout failed:`, error);
            reject(error);
          }
        } else {
          resolve('pixel_skipped');
        }
      }),

      // Conversions API
      (async () => {
        try {
          const response = await fetchWithRetry(
            '/.netlify/functions/meta-conversion-tracking',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(cleanPayload(payload)),
            }
          );

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
          }

          await response.json();
          debugLog(`✅ Meta Conversions API InitiateCheckout event sent`);
          return 'api_success';
        } catch (error) {
          debugError(`❌ Meta Conversions API InitiateCheckout failed:`, error);
          throw error;
        }
      })(),
    ]);

    debugLog(`📊 InitiateCheckout tracking completed:`, { eventId, results });

    return { success: true, event_id: eventId };
  } catch (error) {
    debugError(`❌ Error tracking Meta InitiateCheckout event:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Generic function to track any event type
 */
export const trackMetaEvent = async (eventType, data) => {
  if (eventType === 'Lead') {
    return trackLead(data);
  } else if (eventType === 'Purchase') {
    return trackPurchase(data);
  } else if (eventType === 'PageView') {
    return trackPageView(data);
  } else if (eventType === 'InitiateCheckout') {
    return trackInitiateCheckout(data);
  } else {
    debugError('Unsupported event type:', eventType);
    return { success: false, error: 'Unsupported event type' };
  }
};

// Export utilities for debugging/testing
export { clearFacebookTrackingSession, getFacebookTrackingParams };
